// src/ui/SearchResults.jsx — the organised result list.
//
// Three tiers, always in this order: chats whose NAME matches, people you could
// start a chat with, then everything found inside conversations — grouped by
// kind (messages, photos, links, documents, voice, polls, …) and, inside each
// kind, by date (Today / Yesterday / weekday / month).
//
// A kind chip row across the top filters to one bucket; the counts are live.

import React, { useMemo, useState } from 'react';
import Avatar, { resolveName, groupTitle } from './Avatar';
import ChatAvatar, { ChatTitle } from './ChatAvatar';
import { Search } from './icons';
import { KINDS, dateBucket } from '../services/searchAll';
import { isNoteToSelf } from '../services/chats';
import { timeLabel } from './format';

/** the matched run, lit — so you can see WHY a row came back */
function Marked({ text, term }) {
  const t = String(text || '');
  const q = String(term || '').trim();
  if (!q) return <>{t}</>;
  const i = t.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{t}</>;
  // keep the match in view on long bodies rather than truncating before it
  const from = Math.max(0, i - 34);
  const head = from > 0 ? '…' : '';
  return (
    <>
      {head}{t.slice(from, i)}<mark>{t.slice(i, i + q.length)}</mark>{t.slice(i + q.length, i + q.length + 90)}
    </>
  );
}

export default function SearchResults({
  term, results, chats, people, myUid, onOpenChat, onOpenMessage, onStartChat,
}) {
  const [kind, setKind] = useState('all');

  const nameHits = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return [];
    return (chats || []).filter((c) => {
      const n = c.isGroup ? groupTitle(c) : (c.participantNames?.[Object.keys(c.participantNames || {}).find((u) => u !== myUid)] || '');
      return String(n).toLowerCase().includes(q)
        || (isNoteToSelf(c, myUid) && 'note to self'.includes(q));
    }).slice(0, 6);
  }, [term, chats, myUid]);

  const present = KINDS.filter((k) => (results.byKind[k.id] || []).length > 0);
  const shown = kind === 'all' ? present : present.filter((k) => k.id === kind);

  const empty = results.total === 0 && nameHits.length === 0 && (people || []).length === 0;

  return (
    <div className="sr">
      {/* kind filter */}
      {present.length > 1 && (
        <div className="sr-chips">
          <button className={`ci-chip ${kind === 'all' ? 'on' : ''}`} onClick={() => setKind('all')}>
            All <em>{results.total}</em>
          </button>
          {present.map((k) => (
            <button key={k.id} className={`ci-chip ${kind === k.id ? 'on' : ''}`} onClick={() => setKind(k.id)}>
              {k.name} <em>{results.byKind[k.id].length}</em>
            </button>
          ))}
        </div>
      )}

      {empty && (
        <div className="sr-empty">
          <span className="sr-empty-ico"><Search width={20} height={20} /></span>
          <b>Nothing found</b>
          <i>Try a name, a word from a message, a file name or a place.</i>
        </div>
      )}

      {/* chats by name */}
      {kind === 'all' && nameHits.length > 0 && (
        <>
          <div className="sr-head">Chats</div>
          {nameHits.map((c) => (
            <button className="sr-row" key={`c-${c.id}`} onClick={() => onOpenChat(c)}>
              <ChatAvatar chat={c} myUid={myUid} size="sm" />
              <span className="sr-body">
                <b><ChatTitle chat={c} myUid={myUid} /></b>
                <i>Open conversation</i>
              </span>
            </button>
          ))}
        </>
      )}

      {/* people to start with */}
      {kind === 'all' && (people || []).length > 0 && (
        <>
          <div className="sr-head">People</div>
          {people.map((u) => (
            <button className="sr-row" key={`p-${u.uid}`} onClick={() => onStartChat(u)}>
              <Avatar profile={u} size="sm" myUid={myUid} />
              <span className="sr-body">
                <b>{resolveName(u)}</b>
                <i>{u.username ? `@${u.username}` : (u.phoneE164 || u.email || 'Start a chat')}</i>
              </span>
            </button>
          ))}
        </>
      )}

      {/* everything inside conversations */}
      {shown.map((k) => {
        const hits = results.byKind[k.id] || [];
        let lastBucket = '';
        return (
          <React.Fragment key={k.id}>
            <div className="sr-head">{k.name} <em>{hits.length}</em></div>
            {hits.slice(0, kind === 'all' ? 8 : 200).map((h) => {
              const bucket = dateBucket(h.ts);
              const showBucket = bucket !== lastBucket;
              lastBucket = bucket;
              return (
                <React.Fragment key={`${h.chatId}-${h.messageId}-${k.id}`}>
                  {showBucket && <div className="sr-date">{bucket}</div>}
                  <button className="sr-row hit" onClick={() => onOpenMessage(h)}>
                    {h.thumb
                      ? <img className="sr-thumb" src={`data:image/jpeg;base64,${h.thumb}`} alt="" />
                      : <ChatAvatar chat={h.chat} myUid={myUid} size="sm" />}
                    <span className="sr-body">
                      <b>
                        <span><ChatTitle chat={h.chat} myUid={myUid} /></span>
                        <em className="sr-kind">{h.label}</em>
                      </b>
                      <i><Marked text={h.body} term={term} /></i>
                    </span>
                    <span className="sr-when">{timeLabel(h.ts)}</span>
                  </button>
                </React.Fragment>
              );
            })}
            {kind === 'all' && hits.length > 8 && (
              <button className="sr-more" onClick={() => setKind(k.id)}>
                Show all {hits.length} in {k.name.toLowerCase()}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
